/* Auction Clearance — Shelf Favorite, v1.1.0.
 * Progressive enhancement only. Shopify is authoritative for prices, inventory,
 * cart validation, checkout and customer records. No external services or pixels.
 */
(() => {
  'use strict';
  const config = window.ACTheme || {};
  const root = config.root || '/';
  const cartUrl = config.cart || `${root}cart`;
  const status = (message) => {
    const node = document.getElementById('ACGlobalStatus');
    if (node) node.textContent = message;
  };
  const endpoint = (url) => url.endsWith('.js') ? url : `${url}.js`;
  const supportsDialog = typeof HTMLDialogElement !== 'undefined' && typeof HTMLDialogElement.prototype.showModal === 'function';
  const focusReturn = new WeakMap();
  const bodyLock = () => document.body.classList.toggle('ac-modal-open', Boolean(document.querySelector('dialog[open]')));
  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
    bodyLock();
    const previous = focusReturn.get(dialog);
    if (previous && previous.isConnected) previous.focus({preventScroll:true});
  }
  function openDialog(dialog, trigger = document.activeElement) {
    if (!dialog || !supportsDialog) return false;
    document.querySelectorAll('dialog[open]').forEach((other) => {if (other !== dialog) closeDialog(other);});
    focusReturn.set(dialog, trigger);
    if (!dialog.open) dialog.showModal();
    bodyLock();
    return true;
  }
  document.addEventListener('close', bodyLock, true);
  document.addEventListener('cancel', (event) => {event.preventDefault();closeDialog(event.target);}, true);
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const opener = target.closest('[data-open-dialog]');
    if (opener) {
      if (openDialog(document.getElementById(opener.dataset.openDialog), opener)) event.preventDefault();
      else window.location.assign(`${root}collections/all?view=brands`);
      return;
    }
    const closer = target.closest('[data-close-dialog]');
    if (closer) {event.preventDefault();closeDialog(closer.closest('dialog'));return;}
    if (target.tagName === 'DIALOG') {
      const rect = target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) closeDialog(target);
    }
    const cartLink = target.closest('[data-cart-open]');
    if (cartLink && config.useDrawer && supportsDialog) {event.preventDefault();openDialog(document.getElementById('ACCartDrawer'),cartLink);return;}
    const stepper = target.closest('[data-quantity-step]');
    if (stepper) {
      const input = stepper.closest('.ac-quantity')?.querySelector('input');
      if (!input) return;
      event.preventDefault();
      const minimum = Number(input.min || 1), increment = Number(input.step || 1);
      const maximum = input.max === '' ? Infinity : Number(input.max);
      const value = Number(input.value || minimum) + Number(stepper.dataset.quantityStep) * increment;
      input.value = String(Math.min(maximum,Math.max(minimum,value)));
      input.dispatchEvent(new Event('change',{bubbles:true}));
    }
    document.querySelectorAll('.ac-header .ac-nav-disclosure[open]').forEach((details) => {
      if (!details.contains(target)) details.open = false;
    });
  });
  document.addEventListener('keydown',(event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.ac-nav-disclosure[open]').forEach((details) => {
      if (details.contains(document.activeElement)) details.querySelector('summary')?.focus();
      details.open = false;
    });
  });
  document.addEventListener('toggle',(event) => {
    const details = event.target;
    if (!(details instanceof Element) || !details.matches('.ac-header .ac-nav-disclosure[open]')) return;
    document.querySelectorAll('.ac-header .ac-nav-disclosure[open]').forEach((other) => {if(other!==details)other.open=false;});
  },true);

  /* Cart responses are server-rendered. A successful mutation is never retried. */
  let cartBusy = false;
  async function replaceCart(sectionHTML, shouldOpen, trigger) {
    let html = sectionHTML;
    if (!html) {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id','ac-cart-drawer');
      const response = await fetch(url,{credentials:'same-origin',headers:{Accept:'text/html'}});
      if (!response.ok) throw new Error('Unable to refresh cart.');
      html = await response.text();
    }
    const parsed = new DOMParser().parseFromString(html,'text/html');
    const replacement = parsed.querySelector('#ACCartDrawer');
    const old = document.getElementById('ACCartDrawer');
    if (!replacement || !old) throw new Error('Unable to render cart.');
    const wasOpen = old.open;
    const returnElement = focusReturn.get(old) || trigger;
    if (old.open) old.close();
    const next = document.importNode(replacement,true);
    old.replaceWith(next);
    const count = Number(next.dataset.itemCount || 0);
    document.querySelectorAll('[data-cart-count]').forEach((element) => element.textContent = String(count));
    if (shouldOpen || wasOpen) openDialog(next,returnElement);
    else bodyLock();
    document.dispatchEvent(new CustomEvent('ac:cart-updated',{detail:{itemCount:count}}));
    return next;
  }
  function readError(payload, fallback) {
    if (typeof payload?.description === 'string') return payload.description;
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.errors === 'string') return payload.errors;
    return fallback;
  }
  function showFormError(form,message) {
    let node = form.querySelector('[data-form-error]');
    if (!node && form.closest('#ACCartDrawer')) node = document.querySelector('#ACCartDrawer [data-cart-error]');
    if (node) {node.textContent=message;node.hidden=false;}
    status(message);
  }
  document.addEventListener('submit',async(event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const isAdd = form.matches('[data-product-form]');
    const isChange = form.matches('[data-cart-change]');
    if (!isAdd && !isChange) return;
    // Native navigation avoids leaving a stale full-cart form behind after a drawer edit.
    if (isChange && document.body.classList.contains('template-cart')) return;
    if (!window.fetch || !window.FormData) return;
    event.preventDefault();
    if (cartBusy) {status('A cart update is already in progress.');return;}
    if (!form.reportValidity()) return;
    cartBusy=true;
    const button=event.submitter || form.querySelector('button[type="submit"]');
    const originalDisabled=button?.disabled || false;
    const errorNode=form.querySelector('[data-form-error]');
    if(errorNode){errorNode.hidden=true;errorNode.textContent='';}
    if(button){button.disabled=true;button.setAttribute('aria-busy','true');}
    let data=new FormData(form);
    data.set('sections','ac-cart-drawer');
    data.set('sections_url',window.location.pathname);
    let url=endpoint(isAdd?(config.cartAdd||`${root}cart/add`):(config.cartChange||`${root}cart/change`));
    try {
      let extraHeaders={};
      if(isAdd&&window.ACAddonPacking)data=window.ACAddonPacking.prepareAdd(form,data);
      if(isChange&&window.ACAddonPacking){const prepared=await window.ACAddonPacking.prepareChange(data);url=prepared.url;data=prepared.body;extraHeaders=prepared.headers;}
      const response=await fetch(url,{method:'POST',body:data,credentials:'same-origin',headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest',...extraHeaders}});
      let payload;
      try{payload=await response.json();}
      catch{window.location.assign(cartUrl);return;}
      if(!response.ok || payload.status>=400){showFormError(form,readError(payload,'Shopify could not update this item. Review the selected option and quantity.'));return;}
      // From this point the server confirmed success. Failure to render is not an add failure.
      if(!config.useDrawer || !supportsDialog){window.location.assign(cartUrl);return;}
      try{await replaceCart(payload.sections?.['ac-cart-drawer'],true,button);status(isAdd?'Added to your cart.':'Cart updated.');}
      catch{window.location.assign(cartUrl);}
    }catch(error){
      showFormError(form,'We couldn’t confirm the cart update. Check your cart before trying again, so you don’t add the item twice.');
    }finally{
      cartBusy=false;
      if(button?.isConnected){const product=button.closest('ac-product');const selected=product?.variant;button.disabled=selected?(!selected.available||(product.requiresPlan&&!(selected.plans||[]).length)):originalDisabled;button.removeAttribute('aria-busy');}
    }
  });

  /* Search works as a normal GET form when the suggestion service is unavailable. */
  class ACSearch extends HTMLElement {
    connectedCallback(){
      if(this.ready)return;this.ready=true;
      this.input=this.querySelector('input[type="search"]');
      this.results=this.querySelector('.ac-search-results');
      this.live=this.querySelector('[role="status"]');
      this.timer=null;this.fetchController=null;this.events=new AbortController();
      if(!this.input||!this.results||this.dataset.enabled!=='true')return;
      const opts={signal:this.events.signal};
      this.input.addEventListener('input',()=>{
        clearTimeout(this.timer);this.fetchController?.abort();
        if(this.input.value.trim().length<2){this.hide();return;}
        this.timer=setTimeout(()=>this.suggest(this.input.value.trim()),280);
      },opts);
      this.input.addEventListener('keydown',(event)=>{
        if(event.key==='ArrowDown'&&!this.results.hidden){const first=this.results.querySelector('a');if(first){event.preventDefault();first.focus();}}
        if(event.key==='Escape')this.hide();
      },opts);
      this.results.addEventListener('keydown',(event)=>{
        const links=Array.from(this.results.querySelectorAll('a'));
        const index=links.indexOf(document.activeElement);
        if(event.key==='ArrowDown'&&index<links.length-1){event.preventDefault();links[index+1]?.focus();}
        if(event.key==='ArrowUp'){event.preventDefault();if(index<=0)this.input.focus();else links[index-1]?.focus();}
        if(event.key==='Escape'){event.preventDefault();this.input.focus();this.hide();}
      },opts);
      this.addEventListener('focusout',(event)=>{if(!this.contains(event.relatedTarget))this.hide();},opts);
      this.input.addEventListener('focus',()=>{if(this.results.childElementCount&&this.input.value.trim().length>1)this.results.hidden=false;},opts);
    }
    disconnectedCallback(){clearTimeout(this.timer);this.fetchController?.abort();this.events?.abort();this.ready=false;}
    hide(){if(this.results)this.results.hidden=true;if(this.live)this.live.textContent='';}
    async suggest(query){
      this.fetchController=new AbortController();
      const url=new URL(config.predictive||`${root}search/suggest`,window.location.origin);
      url.searchParams.set('q',query);url.searchParams.set('section_id','ac-predictive-search');
      url.searchParams.set('resources[type]','product,collection');url.searchParams.set('resources[limit]','6');
      try{
        const response=await fetch(url,{signal:this.fetchController.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
        if(!response.ok)throw new Error('Search suggestions unavailable.');
        const html=await response.text();
        if(query!==this.input.value.trim())return;
        const parsed=new DOMParser().parseFromString(html,'text/html');
        const content=parsed.querySelector('[data-predictive-content]');
        if(!content){this.hide();return;}
        this.results.replaceChildren(document.importNode(content,true));
        this.results.hidden=false;
        if(this.live)this.live.textContent=`${Number(content.dataset.count||0)} product suggestions. Press Down Arrow to explore, or Enter for all results.`;
      }catch(error){if(error.name!=='AbortError'){this.hide();if(this.live)this.live.textContent='Press Enter to search the full catalog.';}}
    }
  }

  class ACProduct extends HTMLElement {
    connectedCallback(){
      if(this.ready)return;this.ready=true;this.events=new AbortController();
      const opts={signal:this.events.signal};
      this.form=this.querySelector('[data-product-form]');
      this.select=this.querySelector('[data-variant-select]');
      this.plan=this.querySelector('[data-selling-plan]');
      this.requiresPlan=this.dataset.requiresPlan==='true';
      this.slides=Array.from(this.querySelectorAll('[data-media-id]'));
      try{this.variants=JSON.parse(this.querySelector('[data-product-variants]')?.textContent||'[]');}catch{this.variants=[];}
      if(this.slides.length){this.showMedia(this.slides[0].dataset.mediaId);this.dataset.galleryReady='true';}
      this.select?.addEventListener('change',()=>this.selectVariant(true),opts);
      this.plan?.addEventListener('change',()=>this.updatePrice(),opts);
      this.querySelectorAll('[data-media-select]').forEach((link)=>link.addEventListener('click',(event)=>{event.preventDefault();this.showMedia(link.dataset.mediaSelect);},opts));
      this.querySelectorAll('[data-image-zoom]').forEach((link)=>link.addEventListener('click',(event)=>{
        const dialog=this.querySelector('[data-image-dialog]');const image=dialog?.querySelector('[data-zoom-image]');
        if(!image||!supportsDialog)return;
        event.preventDefault();image.src=link.href;image.alt=link.closest('figure')?.querySelector('img')?.alt||'';openDialog(dialog,link);
      },opts));
      this.querySelectorAll('[data-load-media],[data-load-model]').forEach((button)=>button.addEventListener('click',()=>{
        const parent=button.parentElement;const template=parent.querySelector('template');if(!template)return;
        const isModel=button.hasAttribute('data-load-model');parent.replaceChildren(template.content.cloneNode(true));
        if(isModel&&window.Shopify?.loadFeatures){window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:()=>{
          const model=parent.querySelector('model-viewer');if(model&&window.Shopify.ModelViewerUI)new window.Shopify.ModelViewerUI(model);
        }}]);}
      },opts));
      this.querySelector('[data-sticky-add]')?.addEventListener('click',()=>{
        if(this.form?.requestSubmit)this.form.requestSubmit(this.form.querySelector('[data-add-button]'));
        else this.form?.querySelector('[data-add-button]')?.click();
      },opts);
      this.selectVariant(false);
      const primary=this.form?.querySelector('[data-add-button]');
      const sticky=this.querySelector('[data-sticky-purchase]');
      if(primary&&sticky&&this.dataset.sticky==='true'){
        const updateSticky=()=>{
          const rect=primary.getBoundingClientRect();
          const show=rect.bottom<0&&window.matchMedia('(max-width:989px)').matches;
          sticky.hidden=!show;document.body.classList.toggle('ac-body-with-sticky',show);
        };
        let frame=0;
        const requestSticky=()=>{if(frame)return;frame=requestAnimationFrame(()=>{frame=0;updateSticky();});};
        window.addEventListener('scroll',requestSticky,{passive:true,signal:this.events.signal});
        window.addEventListener('resize',requestSticky,{passive:true,signal:this.events.signal});
        if('IntersectionObserver'in window){this.stickyObserver=new IntersectionObserver(updateSticky,{threshold:0.1});this.stickyObserver.observe(primary);}
        updateSticky();
      }
    }
    disconnectedCallback(){this.events?.abort();this.stickyObserver?.disconnect();document.body.classList.remove('ac-body-with-sticky');this.ready=false;}
    showMedia(id){
      const target=this.slides.find((slide)=>String(slide.dataset.mediaId)===String(id));if(!target)return;
      this.slides.forEach((slide)=>{
        const active=slide===target;slide.hidden=!active;
        if(!active){slide.querySelectorAll('video').forEach((video)=>video.pause());
          slide.querySelectorAll('iframe').forEach((iframe)=>{
            try{
              const origin=new URL(iframe.src).origin;
              if(origin.includes('youtube'))iframe.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}),origin);
              if(origin==='https://player.vimeo.com')iframe.contentWindow?.postMessage(JSON.stringify({method:'pause'}),origin);
            }catch{/* Ignore unsupported embedded media. */}
          });
        }
      });
      this.querySelectorAll('[data-media-select]').forEach((thumb)=>thumb.setAttribute('aria-current',String(String(thumb.dataset.mediaSelect)===String(id))));
    }
    selectVariant(updateURL){
      const variant=this.variants.find((item)=>String(item.id)===String(this.select?.value));
      if(!variant)return;this.variant=variant;
      const quantity=this.querySelector('[data-quantity]');
      if(quantity){
        quantity.min=String(variant.min||1);quantity.step=String(variant.step||1);
        if(variant.max!==null&&variant.max!==undefined)quantity.max=String(variant.max);else quantity.removeAttribute('max');
        let value=Math.max(Number(quantity.value)||1,Number(variant.min||1));
        if(quantity.max!=='')value=Math.min(value,Number(quantity.max));
        const min=Number(quantity.min);const step=Number(quantity.step);
        quantity.value=String(min+Math.floor((value-min)/step)*step);
      }
      if(this.plan){
        const old=this.plan.value;this.plan.replaceChildren();
        if(!this.requiresPlan)this.plan.add(new Option('One-time purchase',''));
        (variant.plans||[]).forEach((plan)=>this.plan.add(new Option(`${plan.name} — ${plan.priceFormatted}`,String(plan.id))));
        if(Array.from(this.plan.options).some((option)=>option.value===old))this.plan.value=old;
      }
      const sku=this.querySelector('[data-variant-sku]');if(sku)sku.textContent=variant.sku||'';
      const barcode=this.querySelector('[data-variant-barcode]');if(barcode)barcode.textContent=variant.barcode||'';
      const skuRow=this.querySelector('[data-sku-row]');if(skuRow)skuRow.hidden=!variant.sku;
      const barcodeRow=this.querySelector('[data-barcode-row]');if(barcodeRow)barcodeRow.hidden=!variant.barcode;
      const availability=this.querySelector('[data-variant-availability]');
      if(availability){availability.textContent=variant.availability;availability.dataset.available=String(variant.available);}
      const available=variant.available&&(!this.requiresPlan||(variant.plans||[]).length>0);
      this.querySelectorAll('[data-add-button],[data-sticky-add]').forEach((button)=>button.disabled=!available);
      const label=this.querySelector('[data-add-label]');if(label)label.textContent=available?'Add to cart':'Sold out';
      const stickyButton=this.querySelector('[data-sticky-add]');if(stickyButton)stickyButton.textContent=available?'Add to cart':'Sold out';
      this.updatePrice();
      this.dispatchEvent(new CustomEvent('ac:variant-changed'));
      if(variant.mediaId)this.showMedia(variant.mediaId);
      if(updateURL){const url=new URL(window.location.href);url.searchParams.set('variant',String(variant.id));history.replaceState({},'',url.toString());}
      const error=this.querySelector('[data-form-error]');if(error){error.hidden=true;error.textContent='';}
    }
    updatePrice(){
      if(!this.variant)return;
      const plan=(this.variant.plans||[]).find((item)=>String(item.id)===String(this.plan?.value));
      const formatted=plan?plan.priceFormatted:this.variant.priceFormatted;
      const price=this.querySelector('[data-variant-price]');if(price)price.textContent=formatted;
      const stickyPrice=this.querySelector('[data-sticky-price]');if(stickyPrice)stickyPrice.textContent=formatted;
      const compare=this.querySelector('[data-variant-compare]');
      if(compare){compare.hidden=Boolean(plan)||!(this.variant.comparePrice>this.variant.price);compare.textContent=this.variant.compareFormatted||'';}
    }
  }

  class ACRecommendations extends HTMLElement {
    connectedCallback(){
      if(this.loaded||!this.dataset.url)return;
      const load=()=>{this.loaded=true;this.load();};
      if('IntersectionObserver'in window){this.observer=new IntersectionObserver((entries)=>{if(entries.some((entry)=>entry.isIntersecting)){this.observer.disconnect();load();}},{rootMargin:'250px'});this.observer.observe(this);}else load();
    }
    disconnectedCallback(){this.observer?.disconnect();this.controller?.abort();}
    async load(){
      this.controller=new AbortController();
      try{
        const response=await fetch(this.dataset.url,{credentials:'same-origin',signal:this.controller.signal});
        if(!response.ok)return;
        const documentHTML=new DOMParser().parseFromString(await response.text(),'text/html');
        const replacement=documentHTML.querySelector('ac-recommendations');
        if(replacement?.querySelector('.ac-product-card'))this.replaceChildren(...Array.from(replacement.childNodes).map((node)=>document.importNode(node,true)));
      }catch{/* Related products are optional; core shopping remains available. */}
    }
  }
  if(!customElements.get('ac-search'))customElements.define('ac-search',ACSearch);
  if(!customElements.get('ac-product'))customElements.define('ac-product',ACProduct);
  if(!customElements.get('ac-recommendations'))customElements.define('ac-recommendations',ACRecommendations);
  function initializeFilters(context=document){
    context.querySelectorAll('[data-filter-panel]').forEach((panel)=>{
      if(panel.dataset.initialized)return;panel.dataset.initialized='true';
      if(window.matchMedia('(max-width:989px)').matches)panel.open=false;
    });
  }
  initializeFilters();
  document.addEventListener('shopify:section:load',(event)=>initializeFilters(event.target));
  document.addEventListener('shopify:section:unload',()=>{bodyLock();document.body.classList.remove('ac-body-with-sticky');});
})();
